import React, { useMemo } from 'react';
import { Flex, Tooltip } from 'antd';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBell, faClipboardCheck, faClipboardQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { groupBy, mapValues } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExRechartsTooltipContent } from 'jf/components/DevExRechartsTooltipContent';
import { DevExSkeleton } from 'jf/components/DevExSkeleton';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useStudyParticipation } from '../useStudyParticipation';

const CHART_SPAN_DAYS = 12;

type ParticipationDatum = {
  value?: number;
  dateTimeMillis: number;
  dateTime: DateTime;
};

type ParticipationChartProps = {
  studyRef: string | undefined;
  teamRef?: string;
};

// official type for viewBox seems wrong? so casting it as any here
export const EventLabel: React.FC<{ icon: IconProp; viewBox: any; tooltip: string }> = ({
  viewBox,
  tooltip,
  icon,
}) => {
  const theme = useDevExTheme();
  // diameter / width of icon
  // this code is just finding the center of the icon
  // and translating the icon centered over the line
  const d = 15;
  const r = d / 2;
  const tx = viewBox.x - r;
  const ty = -viewBox.height / 2 - d;
  const transform = `translate(${tx}, ${ty})`;

  return (
    <Tooltip title={tooltip}>
      <g
        transform={transform}
        style={{
          cursor: 'pointer',
          width: d,
          height: d,
        }}
      >
        {/* This adds an element to hover for the tooltip behind the transparent fontawesome icon */}
        <rect y={-1 * ty + d / 2} width={d} height={d} fillOpacity={0} />
        <FontAwesomeIcon
          color={theme.color.text.secondary}
          strokeWidth={100}
          strokeOpacity={0}
          width={d}
          icon={icon}
        />
      </g>
    </Tooltip>
  );
};
export const ParticipationChart: React.FC<ParticipationChartProps> = (props) => {
  const theme = useDevExTheme();

  const { data: participation } = useStudyParticipation({
    studyRef: props.studyRef,
    teamRef: props.teamRef,
  });

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: props.studyRef });
  const flags = useClientFlags();

  const data = useMemo(() => {
    if (!participation) {
      return undefined;
    }

    const responseDateTimes = participation.responseTimes.map((iso) => DateTime.fromISO(iso));

    // count responses in hourly buckets
    const responseCountsByHour = mapValues(
      groupBy(responseDateTimes, (dateTime) => dateTime.startOf('hour').toISO()!),
      (dateTimes) => dateTimes.length
    );

    // day the study opened
    const openDateTime = DateTime.fromISO(participation.openDate);
    // day after last response was received (or today if it is yet to close)
    const closeDateTimeReference =
      participation.closeDate && responseDateTimes.length
        ? responseDateTimes.at(-1)!
        : DateTime.now();
    let closeDateTime = closeDateTimeReference.startOf('day').plus({ days: 1 });
    if (flags.scheduledCloseReminders) {
      if (study?.scheduledCloseDate) {
        closeDateTime = DateTime.fromISO(study.scheduledCloseDate, { zone: 'utc' })
          .set({ hour: 9 })
          .plus({
            days: 1,
          });
      }
    }

    // end chart the day after closeDateTime
    const endDateTime = closeDateTime.plus({ days: 1 });

    // start chart the day before openDateTime, but limit range to CHART_SPAN_DAYS
    const minStartDateTime = openDateTime.minus({ days: 1 });
    const maxStartDateTime = endDateTime.minus({ days: CHART_SPAN_DAYS });
    const startDateTime =
      flags.scheduledCloseReminders && study?.scheduledCloseDate
        ? minStartDateTime
        : DateTime.max(minStartDateTime, maxStartDateTime);

    // count responses that fall outside of chart bounds
    const excludedResponseCount = responseDateTimes.filter(
      (dateTime) => dateTime < startDateTime
    ).length;

    const data: ParticipationDatum[] = [];
    const currentHour = DateTime.now().startOf('hour');

    // keep running incrementors of hour and response count
    let chartHour = startDateTime;
    let chartResponseCount = excludedResponseCount;

    // iterate through hours between startDateTime and endDateTime
    while (chartHour <= endDateTime) {
      // add responses in this hour (if any) to the running count
      const hourResponseCount = responseCountsByHour[chartHour.toISO()!];
      chartResponseCount += hourResponseCount ?? 0;

      // determine if we want to plot a data point for this hour
      const plotDataPoint =
        hourResponseCount || // plot if new responses were gained this hour
        chartHour.hour % 6 === 0 || // plot every 6 hours for labels and tooltips
        chartHour.valueOf() === currentHour.valueOf(); // plot current time

      if (plotDataPoint) {
        // use null value for data points that fall before launchDateTime or after current time
        const nullValue =
          chartHour < openDateTime || chartHour > currentHour || chartHour > closeDateTime;

        data.push({
          value: nullValue ? undefined : chartResponseCount,
          dateTimeMillis: chartHour.valueOf(),
          dateTime: chartHour,
        });
      }

      // increment hour
      chartHour = chartHour.plus({ hours: 1 });
    }
    return data;
  }, [participation, study]);

  const empty = participation && !participation.responseTimes.length;
  const openDateTime = participation && DateTime.fromISO(participation.openDate);

  const getYAxisTickWidth = (): number => {
    const charWidth = 10;
    // the +1 is adding a character padding in case axis rounds to nearest 1000 or 100
    const datacount = String(participation?.responseTimes.length).length + 1;
    return empty || !participation?.responseTimes ? 0 : datacount * charWidth;
  };

  return (
    <Flex>
      {data && study ? (
        <ResponsiveContainer width={'100%'} height={200}>
          {/* Margin top 30 to make room for event icons */}
          <AreaChart data={data} margin={{ left: 0, top: 30, right: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={theme.color.visualization.grid} />
            <RechartsTooltip
              content={(tooltipProps) => (
                <DevExRechartsTooltipContent
                  {...tooltipProps}
                  formatLabel={(dateTimeMillis) =>
                    dateTimeMillis &&
                    DateTime.fromMillis(dateTimeMillis).toLocaleString(DateTime.DATETIME_FULL)
                  }
                  formatValue={(value) => `${value} responses`}
                />
              )}
            />
            <XAxis
              dataKey="dateTimeMillis"
              domain={['auto', 'auto']}
              scale="time"
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.color.text.secondary }}
              interval={0} // render every label through tickFormatter
              tickFormatter={(dateTimeMillis) => {
                const dateTime = DateTime.fromMillis(dateTimeMillis);
                // render a label in center of each weekday
                if (dateTime.weekday < 6 && dateTime.hour === 12) {
                  return dateTime.toFormat('MMM d');
                }
                return '';
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.color.text.secondary }}
              allowDecimals={false}
              tickCount={4}
              width={empty ? 0 : getYAxisTickWidth()}
            />
            {
              // create a ReferenceLine for when the study was opened
              openDateTime && (
                <ReferenceLine
                  x={openDateTime.valueOf()}
                  stroke={theme.color.text.tertiary}
                  strokeDasharray="3 3"
                  label={
                    <Label
                      content={({ viewBox }) => {
                        if (!viewBox) {
                          return;
                        }
                        return (
                          <EventLabel
                            viewBox={viewBox}
                            tooltip={`Survey Opened on ${openDateTime.toFormat('cccc, MMM d, yyyy')}`}
                            icon={faClipboardQuestion}
                          />
                        );
                      }}
                    />
                  }
                />
              )
            }
            {
              // create a ReferenceLine for when the study was "closed" (last data point)
              !!participation?.closeDate && (
                <ReferenceLine
                  x={data.filter((datum) => datum.value).at(-1)?.dateTimeMillis}
                  stroke={theme.color.text.tertiary}
                  strokeDasharray="3 3"
                  label={
                    <Label
                      content={({ viewBox }) => {
                        if (!viewBox) {
                          return;
                        }
                        const closedate = DateTime.fromISO(participation.closeDate || '0', {
                          zone: 'utc',
                        });
                        return (
                          <EventLabel
                            viewBox={viewBox}
                            tooltip={`Survey Closed on ${closedate.toFormat('cccc, MMM d, yyyy')}`}
                            icon={faClipboardCheck}
                          />
                        );
                      }}
                    />
                  }
                />
              )
            }
            {study?.schedule &&
              study.schedule.map((event) => {
                // Parse UTC date, get the calendar date, then create new date at 9am Eastern
                const eventDate = DateTime.fromISO(event.date, { zone: 'America/New_York' });
                let strokedasharray = '';
                let icon = faClipboardCheck;
                let tooltip = `Survey will close ${eventDate.toLocaleString(DateTime.DATETIME_FULL)}`;
                if (event.label === 'Reminder') {
                  strokedasharray = '3 3';
                  icon = faBell;
                  tooltip = `Reminder ${eventDate > DateTime.now() ? 'will be ' : ''}sent ${eventDate.toLocaleString(DateTime.DATETIME_FULL)}`;
                  // skip if eventDate is on the same day as scheduled close date
                  if (
                    study?.scheduledCloseDate &&
                    eventDate.hasSame(
                      DateTime.fromISO(study.scheduledCloseDate, { zone: 'utc' }),
                      'day'
                    )
                  ) {
                    return undefined;
                  }
                } else if (participation?.closeDate && event.label === 'Survey Close Date') {
                  // if the survey is closed don't render the close date event
                  return undefined;
                }
                return (
                  <ReferenceLine
                    key={eventDate.valueOf()}
                    x={eventDate.valueOf()}
                    stroke={theme.color.visualization.accent}
                    strokeWidth={2}
                    strokeDasharray={strokedasharray}
                    label={
                      <Label
                        content={({ viewBox }) => {
                          if (!viewBox) {
                            return;
                          }
                          return <EventLabel viewBox={viewBox} tooltip={tooltip} icon={icon} />;
                        }}
                      />
                    }
                  />
                );
              })}
            {
              // create a ReferenceArea to shade in each weekend day
              data.map(
                (datum, i) =>
                  datum.dateTime.hour === 0 &&
                  datum.dateTime.weekday > 5 && (
                    <ReferenceArea
                      key={i}
                      x1={datum.dateTimeMillis}
                      x2={DateTime.fromMillis(datum.dateTimeMillis).plus({ days: 1 }).valueOf()}
                      fill={theme.color.visualization.grid}
                    />
                  )
              )
            }
            <Area
              type="stepAfter"
              dataKey="value"
              stroke={theme.color.visualization.palette[1].data}
              fillOpacity={0.2}
              fill={theme.color.visualization.palette[1].data}
              animationDuration={1000}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <DevExSkeleton height={200} />
      )}
    </Flex>
  );
};
